import { ChangeDetectionStrategy, Component, Inject, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { GitBranchWithAheadBehindStatus, GitCommit, GitCompareDirection, GitQuery, GitService } from '@y42/git';
import { BehaviorSubject, combineLatest, debounceTime, Observable, of, scan, startWith, switchMap, tap } from 'rxjs';
import { GitCommitsListComponent } from './git-commits-list/git-commits-list.component';

const PAGE_SIZE = 40;

@UntilDestroy()
@Component({
  selector: 'y42-git-history-dialog.block.h-full',
  templateUrl: './git-history-dialog.component.html',
  styleUrls: ['./git-history-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitHistoryDialogComponent {
  constructor(
    private readonly gitService: GitService,
    private readonly gitQuery: GitQuery,
    @Inject(MAT_DIALOG_DATA) public params: GitHistoryDialogParams | null,
    private readonly dialogRef: MatDialogRef<GitHistoryDialogComponent>,
  ) {}

  @ViewChild(GitCommitsListComponent) gitCommitsListComponent!: GitCommitsListComponent;

  showBranchList = false;

  resettingScroll = false;

  private readonly offset$ = new BehaviorSubject(0);

  readonly branchToCompareWith$ = new BehaviorSubject<GitBranchWithAheadBehindStatus | null>(null);

  readonly compareDirectionFC = new FormControl<GitCompareDirection>('behind', { nonNullable: true });

  readonly state = connectState(this, {
    activeBranch: this.gitQuery.activeBranch$,
    branches: this.params?.filePath
      ? of([])
      : (this.gitQuery
          .selectAll({
            filterBy: (branch) => {
              const activeBranch = this.gitQuery.getActiveBranch();
              return branch !== activeBranch;
            },
          })
          .pipe(
            switchMap((branches) =>
              Promise.all(
                branches.map(async (branch) => ({
                  ...branch,
                  aheadBehindStatus: await this.gitService.getComparedAheadBehindStatus(branch),
                })),
              ),
            ),
          ) as Observable<GitBranchWithAheadBehindStatus[]>),
    commits: this.gitQuery.gitStatus$.pipe(
      switchMap(() =>
        combineLatest([
          this.compareDirectionFC.valueChanges.pipe(startWith(this.compareDirectionFC.value)),
          this.branchToCompareWith$,
        ]).pipe(
          tap(() => this.resetScroll()),
          switchMap(() =>
            this.offset$.pipe(
              debounceTime(0),
              switchMap((offset) => this.getHistory(offset)),
              scan<GitCommit[], GitCommit[]>((acc, batch) => [...acc, ...batch], []),
              tap((items) => {
                this.resettingScroll = false;
                if (!this.activeCommit && items.length) {
                  this.activeCommit = items[0] ?? null;
                }
              }),
            ),
          ),
        ),
      ),
    ),
  });

  activeCommit: GitCommit | null = null;

  branchItemContext!: { $implicit: GitBranchWithAheadBehindStatus };

  nextBatch() {
    this.offset$.next(this.offset$.value + PAGE_SIZE);
  }

  selectBranchToCompare(branchToCompare: GitBranchWithAheadBehindStatus | null) {
    this.compareDirectionFC.setValue('behind');
    this.branchToCompareWith$.next(branchToCompare);
    this.showBranchList = false;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  private getHistory(offset: number): Promise<GitCommit[]> {
    const branchToCompareWith = this.branchToCompareWith$.value;
    if (branchToCompareWith) {
      const direction = this.compareDirectionFC.value;
      return this.gitService.getComparedHistory(branchToCompareWith, direction, offset, PAGE_SIZE);
    }
    return this.gitService.getHistory(offset, PAGE_SIZE, this.params?.filePath);
  }

  private resetScroll() {
    this.resettingScroll = true;
    this.activeCommit = null;
    this.offset$.next(0);
    this.gitCommitsListComponent?.reset();
  }
}

export interface GitHistoryDialogParams {
  filePath?: string;
}
